import { jwtDecode } from 'jwt-decode';
import { pick } from 'lodash';
import fetch from '../utils/fetch';
import CustomError from '../utils/CustomError';
import {
  loginEndpointV3,
  logoutEndpointV3,
  refreshEndpointV3,
  forgotPasswordEndpointV3,
  userChangePasswordEndpointV3,
  passwordRecoverEndpointV3,
  userProfileEndpointV3
} from '../constants/endpoints';
import {
  UNEXPECTED_ERROR,
  RESET_PWD_SUCCESS,
  LOGIN_SUCCESS,
  REFRESH_SUCCESS,
  TRANSACTION_ID
} from '../constants/services';

export async function defineUserProfile(token) {
  try {
    const query = await fetch(userProfileEndpointV3, {
      method: 'get',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
        Authorization: `Bearer ${token}`
      }
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();
    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }
    const userProfile = pick(json.data, [
      'sub',
      'companyId',
      'email_verified',
      'phone_number_verified',
      'groups'
    ]);
    return userProfile;
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
  }
  // log error
  throw new CustomError(UNEXPECTED_ERROR);
}

export async function login({ username, password, poolName }) {
  try {
    if (!poolName) {
      throw new CustomError({
        code: 'poolNameNeeded',
        message: 'The poolName is invalid'
      });
    }
    if (!username) {
      throw new CustomError({
        code: 'formError',
        message: 'The username is invalid'
      });
    }
    if (!password) {
      throw new CustomError({
        code: 'formError',
        message: 'the password is invalid'
      });
    }

    const query = await fetch(loginEndpointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username,
        password
      }),
      params: { poolName }
    });

    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    const decoded = jwtDecode(json.data.accessToken);

    return {
      status: LOGIN_SUCCESS,
      accessToken: json.data.accessToken,
      refreshToken: json.data.refreshToken,
      expiresIn: json.data.expiresIn,
      tokenType: json.data.tokenType,
      issuedAt: decoded.exp,
      tokenPayload: decoded
    };
  } catch (err) {
    if (err.code === 1003) {
      throw new CustomError({
        code: 1003,
        message: 'Please change your password'
      });
    }
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function ssoLogin(token, poolName) {
  try {
    if (!poolName) {
      throw new CustomError({
        code: 'poolNameNeeded',
        message: 'The poolName is invalid'
      });
    }
    if (!token) {
      throw new CustomError({
        code: 'tokenNeeded',
        message: 'The token is invalid'
      });
    }

    const query = await fetch(`${loginEndpointV3}/sso`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      params: { poolName }
    });

    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    const decoded = jwtDecode(json.data.accessToken);

    return {
      status: LOGIN_SUCCESS,
      accessToken: json.data.accessToken,
      refreshToken: json.data.refreshToken,
      expiresIn: json.data.expiresIn,
      tokenType: json.data.tokenType,
      issuedAt: decoded.exp,
      tokenPayload: decoded
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function logout(token, refreshToken, poolName) {
  try {
    if (!token) {
      throw new CustomError({ message: 'access token required' });
    }
    const tokenPayload = jwtDecode(token);
    const currentTimeMillis = new Date().getTime();
    const expirationTimeMillis = new Date(tokenPayload.exp * 1000).getTime();
    const expired = expirationTimeMillis - currentTimeMillis <= 0;

    if (expired) {
      return {
        status: {
          code: 'logoutSuccess'
        }
      };
    }
    const data = { refreshToken };
    await fetch(logoutEndpointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(data),
      params: { poolName }
    });
    return {
      status: {
        code: 'logoutSuccess'
      }
    };
  } catch (err) {
    // ignore errors and do the logout no matter what
    return {
      status: {
        code: 'logoutSuccess'
      }
    };
  }
}

export async function forgot({ username, poolName }) {
  try {
    if (!username) {
      throw new CustomError({
        code: 'userNameNeeded',
        message: 'Username or email required'
      });
    }
    if (!poolName) {
      throw new CustomError({
        code: 'poolNameNeeded',
        message: 'The poolName is required'
      });
    }

    const query = await fetch(forgotPasswordEndpointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ username }),
      params: { poolName }
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    return {
      status: {
        code: 'forgotSuccess',
        message: json.data.message['en-US']
      }
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    // log err
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function recover({
  code,
  username,
  newPassword,
  reNewPassword,
  poolName
}) {
  try {
    if (!poolName) {
      throw new CustomError({
        code: 'poolNameNeeded',
        message: 'The poolName is required'
      });
    }
    if (!code) {
      throw new CustomError({
        code: 'formError',
        message: 'The code is required'
      });
    }
    if (!username) {
      return new CustomError({
        code: 'formError',
        message: 'The username is required'
      });
    }
    if (!newPassword) {
      return new CustomError({
        code: 'formError',
        message: 'A new password is required'
      });
    }
    if (!reNewPassword) {
      return new CustomError({
        code: 'formError',
        message: 'The password confirmation is required'
      });
    }
    if (newPassword !== reNewPassword) {
      return new CustomError({
        code: 'formError',
        message: 'Your password and confirmation password do not match'
      });
    }

    const query = await fetch(passwordRecoverEndpointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code,
        username,
        password: newPassword
      }),
      params: { poolName }
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    return {
      status: {
        code: 200,
        message: json.data.message['en-US']
      }
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function passwordReset({
  username,
  password,
  newPassword,
  reNewPassword,
  poolName
}) {
  try {
    if (!poolName) {
      return Promise.reject(
        new CustomError({ message: 'The poolName is required' })
      );
    }
    if (!username) {
      return Promise.reject(
        new CustomError({ message: 'The username is required' })
      );
    }
    if (!password) {
      return Promise.reject(
        new CustomError({ message: 'The password is required' })
      );
    }
    if (!newPassword) {
      return Promise.reject(
        new CustomError({ message: 'A new password is required' })
      );
    }
    if (!reNewPassword) {
      return Promise.reject(
        new CustomError({ message: 'Password must be confirmed' })
      );
    }
    if (newPassword !== reNewPassword) {
      return Promise.reject(
        new CustomError({
          message: 'Your password and confirmation password do not match'
        })
      );
    }

    const query = await fetch(userChangePasswordEndpointV3, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        username,
        password,
        newPassword
      }),
      params: { poolName }
    });
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = await query.json();

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    const decoded = jwtDecode(json.data.accessToken);

    return {
      status: RESET_PWD_SUCCESS,
      token: json.data.accessToken,
      tokenPayload: decoded
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}

export async function refresh(refreshToken, username, poolName) {
  let rawBody = null;
  try {
    if (!poolName) {
      return Promise.reject(
        new CustomError({
          code: 'poolNameNeeded',
          message: 'The poolName is required'
        })
      );
    }
    if (!refreshToken) {
      throw new CustomError({
        code: 'refreshTokenNeeded',
        message: 'Auth Token error, please login again'
      });
    }
    if (!username) {
      throw new CustomError({
        code: 'usernameNeeded',
        message: 'Auth Token error, please login again'
      });
    }
    const query = await fetch(refreshEndpointV3, {
      method: 'put',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refreshToken,
        username
      }),
      params: { poolName }
    });
    rawBody = await query.text();
    const transactionId = query.headers.get(TRANSACTION_ID);
    const json = rawBody ? JSON.parse(rawBody) : {};

    if (json.error) {
      throw new CustomError({ ...json, transactionId });
    }

    const decoded = jwtDecode(json.data.accessToken);

    return {
      status: REFRESH_SUCCESS,
      accessToken: json.data.accessToken,
      refreshToken: json.data.refreshToken,
      expiresIn: json.data.expiresIn,
      tokenType: json.data.tokenType,
      issuedAt: decoded.exp,
      tokenPayload: decoded
    };
  } catch (err) {
    if (err.isCustomError) {
      throw err;
    }
    throw new CustomError(UNEXPECTED_ERROR);
  }
}
